import { LIMITS } from './protocol.js';
import { SUPPORTED_PROVIDERS, detectProvider } from './providers.js';
/**
 * Алфавит кодов комнат: без символов, которые путают при диктовке
 * и чтении с экрана (0/O, 1/I/l).
 */
const ROOM_CODE_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';
export const ROOM_CODE_LENGTH = 8;
export function isValidRoomCode(code) {
    if (code.length !== ROOM_CODE_LENGTH)
        return false;
    for (const ch of code) {
        if (!ROOM_CODE_ALPHABET.includes(ch))
            return false;
    }
    return true;
}
/** Генерация кода комнаты из криптостойкого источника случайности. */
export function generateRoomCode(randomBytes) {
    const bytes = randomBytes(ROOM_CODE_LENGTH);
    let out = '';
    for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
        out += ROOM_CODE_ALPHABET[(bytes[i] ?? 0) % ROOM_CODE_ALPHABET.length];
    }
    return out;
}
export function sanitizeNickname(raw) {
    const trimmed = raw.replace(/\s+/g, ' ').trim();
    if (trimmed.length === 0)
        return 'Гость';
    return trimmed.slice(0, LIMITS.nicknameMaxLength);
}
export function sanitizeRoomName(raw) {
    const trimmed = raw.replace(/\s+/g, ' ').trim();
    if (trimmed.length === 0)
        return 'Комната';
    return trimmed.slice(0, LIMITS.roomNameMaxLength);
}
export function sanitizeChatText(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return null;
    return trimmed.slice(0, LIMITS.chatMessageMaxLength);
}
export function clampRate(rate) {
    if (!Number.isFinite(rate))
        return 1;
    return Math.min(LIMITS.maxPlaybackRate, Math.max(LIMITS.minPlaybackRate, rate));
}
export function clampPosition(positionMs, durationMs) {
    if (!Number.isFinite(positionMs) || positionMs < 0)
        return 0;
    if (durationMs != null && durationMs > 0) {
        return Math.min(positionMs, durationMs);
    }
    return positionMs;
}
/** 3 725 000 → «1:02:05». Используется в плеере и в системных сообщениях чата. */
export function formatTimecode(ms) {
    if (!Number.isFinite(ms) || ms < 0)
        ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    const mm = String(minutes).padStart(hours > 0 ? 2 : 1, '0');
    const ss = String(seconds).padStart(2, '0');
    return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} Б`;
    const units = ['КБ', 'МБ', 'ГБ', 'ТБ'];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex++;
    }
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}
/** Определяем, нужен ли HLS-плеер, по расширению в адресе. */
export function detectStreamFormat(url) {
    try {
        const path = new URL(url, 'http://placeholder.local').pathname.toLowerCase();
        return path.endsWith('.m3u8') ? 'hls' : 'progressive';
    }
    catch {
        return url.toLowerCase().includes('.m3u8') ? 'hls' : 'progressive';
    }
}
/**
 * Адрес, который в итоге получит плеер.
 *
 * Загруженные файлы и обычные ссылки играются как есть, а помеченные
 * `proxied` идут через сервер комнаты — иначе браузер до них не достучится.
 *
 * @param origin база для мобильного приложения; на сайте не нужна.
 */
export function resolvePlaybackUrl(source, roomCode, origin = '') {
    if (source.kind === 'url' && source.proxied) {
        return `${origin}/api/rooms/${roomCode}/stream`;
    }
    return source.url.startsWith('/') ? `${origin}${source.url}` : source.url;
}
/** Пускаем в плеер только http(s): blob: и data: с чужой стороны нам не нужны. */
export function isSafeMediaUrl(url) {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
}
/** Контейнеры, которые браузеры почти никогда не проигрывают. */
const UNSUPPORTED_EXTENSIONS = ['.mkv', '.avi', '.wmv', '.flv', '.mpg', '.mpeg', '.rmvb'];
/**
 * Разбирает ссылку на видео до того, как её увидят остальные участники.
 *
 * Плеер на неудачную ссылку отвечает одинаковым «формат не поддерживается»,
 * по которому невозможно понять причину. Здесь мы называем её словами.
 *
 * @param pageProtocol протокол страницы ('https:' или 'http:'). Нужен, чтобы
 *   поймать смешанное содержимое: на сайте по https ссылка по http не
 *   загрузится, и браузер не покажет внятной ошибки.
 */
export function checkMediaUrl(url, pageProtocol) {
    const trimmed = url.trim();
    if (!isSafeMediaUrl(trimmed)) {
        return {
            ok: false,
            problem: 'not_a_url',
            message: 'Введите полную ссылку, начинающуюся с http:// или https://',
        };
    }
    // Ссылка на площадку — это нормальный источник: её открывает
    // встроенный плеер самой площадки, а не наш.
    const provider = detectProvider(trimmed);
    if (provider)
        return { ok: true, provider };
    const parsed = new URL(trimmed);
    const pathname = parsed.pathname.toLowerCase();
    if (pageProtocol === 'https:' && parsed.protocol === 'http:') {
        return {
            ok: false,
            problem: 'insecure',
            message: 'Сайт открыт по https, а ссылка — по http. Браузер заблокирует такое видео. ' +
                'Попробуйте ту же ссылку с https:// в начале.',
        };
    }
    const extension = UNSUPPORTED_EXTENSIONS.find((ext) => pathname.endsWith(ext));
    if (extension) {
        return {
            ok: false,
            problem: 'unsupported_container',
            message: `Браузеры не проигрывают ${extension.slice(1).toUpperCase()}. ` +
                'Нужен MP4 (H.264) или WebM — файл можно перекодировать, например, в HandBrake.',
        };
    }
    // Расширения нет вовсе — часто это страница, но бывают и ссылки с токеном
    // вместо имени файла. Предупреждаем, но пробовать не мешаем.
    if (!/\.[a-z0-9]{2,5}$/.test(pathname)) {
        return {
            ok: true,
            isWarning: true,
            problem: 'not_a_file',
            message: 'В ссылке не видно имени файла. Если это страница сайта, а не прямой ' +
                'адрес видео, плеер её не откроет. Известные площадки — ' +
                `${SUPPORTED_PROVIDERS.join(', ')} — открываются сами.`,
        };
    }
    return { ok: true };
}
/** Максимум спойлеров в одном сообщении — дальше разбирать нечего. */
const MAX_SPOILERS = 20;
export function parseSpoilers(text) {
    const parts = [];
    let rest = text;
    let found = 0;
    while (found < MAX_SPOILERS) {
        const open = rest.indexOf('||');
        if (open === -1)
            break;
        const close = rest.indexOf('||', open + 2);
        if (close === -1)
            break;
        const hidden = rest.slice(open + 2, close);
        // `||||` — пустой спойлер: прятать нечего, оставляем как текст.
        if (hidden.length === 0) {
            const head = rest.slice(0, close + 2);
            if (head)
                parts.push({ kind: 'text', text: head });
            rest = rest.slice(close + 2);
            continue;
        }
        if (open > 0)
            parts.push({ kind: 'text', text: rest.slice(0, open) });
        parts.push({ kind: 'spoiler', text: hidden });
        rest = rest.slice(close + 2);
        found += 1;
    }
    if (rest)
        parts.push({ kind: 'text', text: rest });
    return parts.length > 0 ? parts : [{ kind: 'text', text }];
}
/** Есть ли в сообщении хоть один спойлер — для подсказки в ленте. */
export function hasSpoiler(text) {
    return parseSpoilers(text).some((part) => part.kind === 'spoiler');
}
/**
 * Оборачивает выделенный кусок в спойлер и говорит, куда поставить курсор.
 *
 * Ничего не выделено — вставляем пустую пару и ставим курсор между ними:
 * человек продолжает печатать уже внутри спойлера.
 */
export function wrapSpoiler(value, start, end) {
    if (start === end) {
        return { value: `${value.slice(0, start)}||||${value.slice(start)}`, caret: start + 2 };
    }
    return {
        value: `${value.slice(0, start)}||${value.slice(start, end)}||${value.slice(end)}`,
        caret: end + 4,
    };
}
//# sourceMappingURL=util.js.map